import type { SavedFilter } from '@/components/logcat/FilterBar';
import type { LogLevel } from './parser';

/** Display levels persisted independently from the expression DSL. */
export type LevelToggle = Extract<LogLevel, 'V' | 'D' | 'I' | 'W' | 'E'>;
export const ALL_LEVELS: LevelToggle[] = ['V', 'D', 'I', 'W', 'E'];

export interface TabConfig {
  id: string;
  name: string;
  expr: string;
  wrap: boolean;
  autoScroll: boolean;
  paused: boolean;
  /** Visible levels; absent / empty means "all five". */
  levels?: LevelToggle[];
  /** Fold consecutive duplicate lines into a single row with × N. */
  aggregate?: boolean;
  /**
   * 多 term 之间的合成模式。`'and'`（默认 + 与 AS 一致）= 全部满足才命中；
   * `'or'` = 任一满足即命中。配合 FilterBar 的 AND/OR 切换按钮工作；持久
   * 化到 B2 prefs.devices[serial].log.tabs[].combine。
   */
  combine?: 'and' | 'or';
}

/**
 * Parse + sanitize raw tab list from prefs (or any external source).
 * Older payloads may be missing `levels / aggregate / combine`; we
 * fill in defaults instead of dropping them so a partial schema mismatch
 * never wipes the user's tabs.
 */
export function coerceTabs(raw: unknown): TabConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x: unknown): x is TabConfig => {
      if (!x || typeof x !== 'object') return false;
      const o = x as Record<string, unknown>;
      return typeof o.id === 'string' && typeof o.name === 'string' && typeof o.expr === 'string';
    })
    .map((t) => {
      const lv = t.levels;
      const cleaned = Array.isArray(lv)
        ? (lv.filter((x) => ALL_LEVELS.includes(x as LevelToggle)) as LevelToggle[])
        : [];
      return {
        ...t,
        levels: cleaned.length > 0 ? cleaned : ALL_LEVELS.slice(),
        aggregate: typeof t.aggregate === 'boolean' ? t.aggregate : true,
        combine: t.combine === 'or' ? 'or' : 'and',
      };
    });
}

/** Same shape sanity check for the per-device saved-filter list. */
export function coerceSavedFilters(raw: unknown): SavedFilter[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x: unknown): x is SavedFilter => {
    if (!x || typeof x !== 'object') return false;
    const o = x as Record<string, unknown>;
    return (
      typeof o.id === 'string' && typeof o.name === 'string' && typeof o.expr === 'string'
    );
  });
}

export function newTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

export const DEFAULT_TAB: Omit<TabConfig, 'id'> = {
  name: 'Logcat',
  expr: '',
  wrap: false,
  autoScroll: true,
  paused: false,
  levels: ALL_LEVELS.slice(),
  aggregate: true,
  combine: 'and',
};
